// src/app/components/sign-in-form.tsx
'use client';

import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PiArrowRightBold } from 'react-icons/pi';
import { Checkbox, Password, Button, Input } from 'rizzui';
import { loginSchema, LoginSchema } from '@/validators/login.schema';
import { FetchClient } from '@/lib/FetchClient';
import { useRouter } from 'next/navigation';

// Define the expected structure of a successful login response from the API.
interface LoginResponse {
  status: 'success' | 'error';
  user?: {
    privilegeIdx: number;
    // Add other user properties here if needed for redirection or state updates.
  };
  error?: string; // 로그인 실패 시 오류 메시지.
}

const initialValues: LoginSchema = {
  id: '',
  password: '',
  rememberMe: true,
};

export default function SignInForm() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: initialValues,
  });

  const onSubmit: SubmitHandler<LoginSchema> = async (data) => {
    setIsLoading(true);
    try {
      const res = await FetchClient<LoginResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          userId: data.id,
          password: data.password,
        }),
      });

      if (res.status === 'success' && res.user) {
        // A privilegeIdx of 6 is assumed to be an admin role.
        const home = res.user.privilegeIdx === 6 ? '/board/manage-user' : '/board/greeting';
        window.location.href = home; // Redirect after successful login.
      } else {
        // The 'error' property is now correctly typed.
        alert(res.error || 'Login failed. Please check your credentials.');
        reset({ ...data, password: '' });
      }
    } catch (e: any) {
      // Handle network errors or other exceptions from FetchClient.
      console.error("Login submission error:", e);
      // The error object from FetchError might contain more info.
      const errorMessage = e?.info?.error || e.message || '로그인 중 예상치 못한 오류가 발생했습니다.';
      alert(errorMessage);
      reset({ ...data, password: '' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="space-y-5">
        <Input
          type="text"
          size="lg"
          label="아이디"
          placeholder="아이디를 입력해주세요"
          className="[&>label>span]:font-medium"
          inputClassName="text-sm"
          {...register('id')}
          error={errors.id?.message}
        />
        <Password
          label="비밀번호"
          placeholder="비밀번호를 입력해주세요"
          size="lg"
          className="[&>label>span]:font-medium"
          inputClassName="text-sm"
          {...register('password')}
          error={errors.password?.message}
        />
        <div className="flex items-center justify-between pb-2">
          <Checkbox {...register('rememberMe')} label="사용자 기억하기" variant="flat" className="[&>label>span]:font-medium" />
        </div>
        <Button className="w-full" type="submit" size="lg" isLoading={isLoading}>
          <span>로그인</span> <PiArrowRightBold className="ms-2 mt-0.5 h-6 w-6" />
        </Button>
      </div>
    </form>
  );
}
